import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTheme } from '../../context/ThemeContext';

export interface FilteredTotalItem {
  label: string;
  /** Preformatted display value (e.g. already through formatCurrency). */
  value: string;
}

export interface FilteredTotalsProps {
  items: FilteredTotalItem[];
  /** Extra styles for the wrapping strip (e.g. mb overrides). */
  sx?: SxProps<Theme>;
  'data-testid'?: string;
}

/**
 * Compact strip of filter-aware total "tokens" rendered directly under a
 * finance page's filter row. Values are computed by the page from the
 * currently filtered/rendered rows, so the strip live-updates whenever a
 * filter, label chip or date range changes.
 *
 * Always visible on mobile (never display:none), wraps on narrow screens,
 * money aligned with tabular numerals.
 */
const FilteredTotals: React.FC<FilteredTotalsProps> = ({
  items,
  sx,
  'data-testid': dataTestId = 'filtered-totals',
}) => {
  const { theme } = useTheme();

  if (!items || items.length === 0) return null;

  return (
    <Box
      data-testid={dataTestId}
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.75,
        alignItems: 'center',
        mb: 2,
        ...sx,
      }}
    >
      {items.map((item) => (
        <Box
          key={item.label}
          sx={{
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: 0.75,
            px: 1.25,
            py: 0.5,
            borderRadius: '10px',
            backgroundColor: theme.components?.card?.background || '#FFFFFF',
            border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
            minWidth: 0,
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: '0.65rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: theme.gray?.[500] || '#666',
              whiteSpace: 'nowrap',
            }}
          >
            {item.label}
          </Typography>
          <Typography
            component="span"
            sx={{
              fontSize: '1.05rem',
              fontWeight: 700,
              letterSpacing: '-0.01em',
              color: theme.text,
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
            }}
          >
            {item.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default FilteredTotals;
